import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useAdminAuth } from "@/components/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export default function AdminActivation() {
  const { admin } = useAdminAuth();
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [overviewRes, funnelRes] = await Promise.all([
          axios.get(`${API}/admin/activation/overview`, { headers: getAuthHeaders() }),
          axios.get(`${API}/admin/activation/funnel`, { headers: getAuthHeaders() }),
        ]);
        setOverview(overviewRes.data);
        setFunnel(funnelRes.data);
      } catch (error) {
        console.error("Failed to fetch activation data:", error);
        toast.error("Failed to load activation metrics");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Calculate funnel widths based on percentages
  const getFunnelWidth = (pct) => Math.max(20, pct);

  return (
    <div className="space-y-6" data-testid="admin-activation">
      <div>
        <h1 className="text-2xl font-bold text-foreground">User Activation Funnel</h1>
        <p className="text-muted-foreground">Track user journey from signup to first value</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Activation Rate</CardDescription>
            <CardTitle className="text-3xl font-bold text-primary">
              {overview?.activation_rate || 0}%
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {overview?.drop_off?.activated || 0} users activated
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Median Time to First Value</CardDescription>
            <CardTitle className="text-3xl font-bold">
              {overview?.median_time_to_first_value 
                ? `${overview.median_time_to_first_value.toFixed(0)} min`
                : 'N/A'
              }
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              From signup to first analysis
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Users</CardDescription>
            <CardTitle className="text-3xl font-bold">
              {overview?.total_users || 0}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Registered accounts
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Visual Funnel */}
      <Card>
        <CardHeader>
          <CardTitle>Activation Funnel</CardTitle>
          <CardDescription>User progression through key milestones</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {funnel?.funnel?.map((stage, index) => (
            <div key={stage.stage} className="relative">
              <div 
                className="bg-primary/20 rounded-lg transition-all duration-500"
                style={{ 
                  width: `${getFunnelWidth(stage.pct)}%`,
                  minWidth: '150px'
                }}
              >
                <div className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-foreground capitalize">
                      {stage.stage.replace(/_/g, ' ')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {stage.count} users
                    </p>
                  </div>
                  <span className="text-lg font-bold text-primary">
                    {stage.pct}%
                  </span>
                </div>
              </div>
              {index < funnel.funnel.length - 1 && (
                <div className="text-center py-1">
                  <span className="text-muted-foreground text-sm">
                    ↓ {Math.round(((funnel.funnel[index].count - funnel.funnel[index + 1].count) / Math.max(funnel.funnel[index].count, 1)) * 100)}% drop-off
                  </span>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Drop-off Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Current Status Distribution</CardTitle>
          <CardDescription>Where users are in the activation journey</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-red-700">
                {overview?.drop_off?.not_started || 0}
              </p>
              <p className="text-sm text-red-600 font-medium">Not Started</p>
              <p className="text-xs text-muted-foreground mt-1">
                Haven't uploaded a contract
              </p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-yellow-700">
                {overview?.drop_off?.uploaded_no_analysis || 0}
              </p>
              <p className="text-sm text-yellow-600 font-medium">Uploaded Only</p>
              <p className="text-xs text-muted-foreground mt-1">
                Uploaded but no analysis
              </p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-green-700">
                {overview?.drop_off?.activated || 0}
              </p>
              <p className="text-sm text-green-600 font-medium">Activated</p>
              <p className="text-xs text-muted-foreground mt-1">
                Completed first analysis
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
